<template>
  <layout :title="$route.name" :rotas="rotas">
    <v-container fluid class="pa-4 gateway-page">
      <v-card class="gateway-card pa-6" :class="{ 'gateway-card-dark': isDark }" :dark="isDark" max-width="760">
        <div class="gateway-hero">
          <v-avatar :color="gatewayStatusColor" size="54">
            <v-icon dark large>mdi-whatsapp</v-icon>
          </v-avatar>
          <div class="gateway-title">
            <h2>WhatsApp</h2>
            <div class="grey--text">Escolha a instância do Loop que este atendimento vai usar.</div>
          </div>
          <v-spacer />
          <v-chip :color="gatewayStatusColor" dark>{{ gatewayStatusLabel }}</v-chip>
        </div>

        <v-alert v-if="message" :type="messageType" dense text>{{ message }}</v-alert>
        <v-alert v-if="gatewayStatus === 'error' || gatewayStatus === 'auth_failed'" type="error" text>
          Não foi possível manter o gateway conectado.
          <v-btn class="ml-3" small color="error" @click="reconnect">Reconectar</v-btn>
        </v-alert>

        <section v-if="step === 1" class="gateway-step-card">
          <h3 class="mb-1">Aguardando instâncias do Loop</h3>
          <div class="grey--text mb-4">
            Abra o Loop WhatsApp e sincronize as instâncias conectadas com o StonePro.
          </div>
          <div class="gateway-actions">
            <v-btn color="primary" :loading="loadingSessions" @click="loadConfig">
              Atualizar instâncias
            </v-btn>
          </div>
        </section>

        <section v-else-if="step === 2" class="gateway-step-card">
          <h3 class="mb-1">
            {{ sessions.length === 1 ? "Conectando instância" : "Escolha a instância do WhatsApp" }}
          </h3>
          <div class="grey--text mb-4">
            {{ sessions.length === 1 ? "Encontramos uma instância conectada. O StonePro vai entrar nela automaticamente." : "Selecione qual WhatsApp este atendimento vai usar." }}
          </div>

          <div class="session-grid">
            <button
              v-for="session in sessions"
              :key="session.id"
              type="button"
              class="session-card"
              :class="{ selected: config.session_id === session.id }"
              :disabled="saving"
              @click="selectSessionAndConnect(session)"
            >
              <v-icon color="#25D366">mdi-whatsapp</v-icon>
              <span>
                <strong>{{ sessionDisplayName(session) }}</strong>
                <small>{{ sessionSubtitle(session) }}</small>
              </span>
              <v-progress-circular
                v-if="saving && config.session_id === session.id"
                indeterminate
                size="20"
                width="2"
                color="#25D366"
              />
            </button>
          </div>

          <div class="gateway-actions">
            <v-btn text :disabled="saving" @click="loadConfig">Atualizar instâncias</v-btn>
          </div>
        </section>

        <section v-else class="gateway-step-card connected-step">
          <h3 class="mb-1">Conectado</h3>
          <div class="grey--text mb-3">As conversas serão carregadas nesta mesma tela.</div>
          <v-alert type="success" text>
            Recebendo mensagens de <strong>{{ config.session_name || config.session_id }}</strong>.
            <v-btn class="ml-3" small outlined color="error" @click="disconnect">Desconectar</v-btn>
          </v-alert>
        </section>
      </v-card>
    </v-container>
  </layout>
</template>

<script>
import { storeGatewayToken, syncGatewayTokenFromConfig } from "../gatewayClient";
import { getWhatsappGatewayStatus, restartWhatsappGatewayRuntime, stopWhatsappGatewayRuntime } from "../runtime";

function currentTenantId() {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}") || {};
    return String(user?.activeTenant?._id || user?.activeTenant || user?.tenant?.[0]?._id || user?.tenant?.[0] || "").trim();
  } catch {
    return "";
  }
}

export default {
  data() {
    return {
      rotas: [
        { name: "Inicio", path: "/dashboard" },
        { name: "WhatsApp", path: "/whatsapp-atendimento" }
      ],
      config: {
        gateway_host: "",
        gateway_port: 8790,
        gateway_token: "",
        session_id: "",
        session_name: "",
        sections: [],
        receive_private_messages: true,
        receive_group_messages: false
      },
      token: "",
      sessions: [],
      loadingSessions: false,
      connectionVerified: false,
      saving: false,
      message: "",
      messageType: "info",
      gatewayStatus: getWhatsappGatewayStatus(),
      step: 1
    };
  },
  computed: {
    canSave() {
      return Boolean(this.connectionVerified && this.config.session_id);
    },
    isDark() {
      return localStorage.dark === "true";
    },
    gatewayStatusLabel() {
      return {
        connected: "conectado",
        connecting: "conectando",
        auth_failed: "token inválido",
        unreachable: "gateway inacessível",
        error: "erro",
        not_configured: "não configurado",
        disconnected: "desconectado",
        network_mismatch: "rede diferente"
      }[this.gatewayStatus] || this.gatewayStatus;
    },
    gatewayStatusColor() {
      if (this.gatewayStatus === "connected") return "success";
      if (this.gatewayStatus === "connecting") return "warning";
      return "error";
    }
  },
  methods: {
    syncStep() {
      if (this.gatewayStatus === "connected" && this.config.session_id) {
        this.step = 3;
      } else if (this.sessions.length) {
        this.step = 2;
      } else {
        this.step = 1;
      }
    },
    async loadConfig() {
      this.loadingSessions = true;
      this.message = "";
      try {
        const response = await this.$run("marketplaces/get-config-whatsapp-gateway", { tenant_id: currentTenantId() });
        this.config = {
          ...this.config,
          ...(response.data.whatsapp_gateway_config || {}),
          gateway_port: (response.data.whatsapp_gateway_config || {}).gateway_port || this.config.gateway_port || 8790
        };
        this.token = syncGatewayTokenFromConfig(this.config);
        this.sessions = this.normalizeStoredSections(this.config.sections);
        this.connectionVerified = Boolean(this.sessions.length);

        if (!this.sessions.length) {
          this.config.session_id = "";
          this.config.session_name = "";
          this.message = "Nenhuma instância WhatsApp sincronizada pelo Loop ainda.";
          this.messageType = "info";
          this.syncStep();
          return;
        }

        if (this.sessions.length === 1) {
          await this.selectSessionAndConnect(this.sessions[0], { automatic: true });
          return;
        }

        const selectedExists = this.sessions.some(session => session.id === this.config.session_id);
        if (!selectedExists) {
          this.config.session_id = "";
          this.config.session_name = "";
        }
        this.message = "Escolha uma instância para começar o atendimento.";
        this.messageType = "info";
        this.syncStep();
      } catch (error) {
        this.showError(error);
      } finally {
        this.loadingSessions = false;
      }
    },
    applySession(session) {
      this.config.session_id = session.id;
      this.config.session_name = session.name || this.config.session_name || "";
      this.token = session.gateway_token || this.config.gateway_token || this.token;
    },
    async selectSessionAndConnect(session, options = {}) {
      this.applySession(session);
      await this.saveConfig({
        successMessage: options.automatic
          ? "Instância única encontrada. Conectando automaticamente..."
          : "Instância selecionada. Conectando..."
      });
    },
    async saveConfig(options = {}) {
      if (!this.canSave) return;
      this.saving = true;
      this.message = "";
      try {
        const selected = this.sessions.find(session => session.id === this.config.session_id);
        const selectedToken = selected?.gateway_token || this.token;
        storeGatewayToken(selectedToken);
        const response = await this.$run("marketplaces/set-config-whatsapp-gateway", {
          tenant_id: currentTenantId(),
          gateway_host: this.config.gateway_host,
          gateway_port: Number(this.config.gateway_port),
          gateway_token: selectedToken,
          session_id: this.config.session_id,
          session_name: selected?.name || this.config.session_name || "",
          sections: this.sessions,
          receive_private_messages: this.config.receive_private_messages,
          receive_group_messages: this.config.receive_group_messages
        });
        this.config = response.data.whatsapp_gateway_config;
        this.token = selectedToken;
        this.message = options.successMessage || "Instância conectada.";
        this.messageType = "success";
        restartWhatsappGatewayRuntime();
      } catch (error) {
        this.showError(error);
      } finally {
        this.saving = false;
        this.syncStep();
      }
    },
    stateLabel(state) {
      if (state === "connected") return "conectada";
      if (state === "qr") return "aguardando QR Code";
      if (state === "error") return "erro";
      return "desconectada";
    },
    sessionDisplayName(session) {
      return session.account_name || session.name || "WhatsApp";
    },
    sessionSubtitle(session) {
      const parts = [
        session.account_id ? String(session.account_id).split("@")[0] : "",
        this.stateLabel(session.state)
      ].filter(Boolean);
      return parts.join(" · ");
    },
    normalizeStoredSections(sections = []) {
      return (Array.isArray(sections) ? sections : [])
        .map(section => ({
          ...section,
          id: String(section.id || section.section_id || "").trim(),
          name: String(section.name || section.session_name || "").trim(),
          state: String(section.state || "").trim(),
          gateway_token: String(section.gateway_token || section.gatewayToken || "").trim(),
          account_id: String(section.account_id || section.accountId || "").trim(),
          account_name: String(section.account_name || section.accountName || "").trim()
        }))
        .filter(section => section.id && section.gateway_token);
    },
    showError(error) {
      this.message = error?.response?.data?.message || error?.message || "Falha ao configurar gateway.";
      this.messageType = "error";
    },
    handleGatewayStatus(event) {
      this.gatewayStatus = event.detail?.status || "disconnected";
      this.syncStep();
    },
    handleGatewayConfigUpdated(event) {
      const incomingConfig = event.detail?.whatsapp_gateway_config || {};
      this.config = {
        ...this.config,
        ...incomingConfig,
        gateway_port: incomingConfig.gateway_port || this.config.gateway_port || 8790
      };
      this.token = syncGatewayTokenFromConfig(this.config);
      this.sessions = this.normalizeStoredSections(this.config.sections);
      if (this.sessions.length === 1 && !this.config.session_id) {
        void this.selectSessionAndConnect(this.sessions[0], { automatic: true });
        return;
      }
      this.connectionVerified = Boolean(this.sessions.length);
      this.syncStep();
      if (this.config.session_id) restartWhatsappGatewayRuntime();
    },
    disconnect() {
      stopWhatsappGatewayRuntime();
      this.gatewayStatus = "disconnected";
      this.syncStep();
    },
    reconnect() {
      restartWhatsappGatewayRuntime();
    }
  },
  created() {
    void this.loadConfig();
    this.syncStep();
    window.addEventListener("stonepro:whatsapp-gateway-status", this.handleGatewayStatus);
    window.addEventListener("stonepro:whatsapp-gateway-config-updated", this.handleGatewayConfigUpdated);
  },
  beforeDestroy() {
    window.removeEventListener("stonepro:whatsapp-gateway-status", this.handleGatewayStatus);
    window.removeEventListener("stonepro:whatsapp-gateway-config-updated", this.handleGatewayConfigUpdated);
  }
};
</script>

<style scoped>
.gateway-page { min-height: calc(100vh - 2px); display: grid; place-items: center; }
.gateway-card { border-radius: 18px; margin: 0 auto; }
.gateway-hero { display: flex; align-items: center; gap: 16px; margin-bottom: 22px; }
.gateway-title { min-width: 0; }
.gateway-title h2 { margin: 0; line-height: 1.1; }
.gateway-step-card { padding: 8px 2px 0; }
.gateway-actions { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; margin-top: 18px; }
.session-grid { display: grid; gap: 10px; }
.session-card { width: 100%; border: 1px solid rgba(37, 211, 102, 0.22); border-radius: 16px; padding: 14px; display: grid; grid-template-columns: auto minmax(0, 1fr) auto; gap: 12px; align-items: center; text-align: left; color: inherit; background: rgba(37, 211, 102, 0.06); transition: border-color 0.18s ease, background 0.18s ease, transform 0.18s ease; }
.session-card:hover, .session-card.selected { border-color: rgba(37, 211, 102, 0.72); background: rgba(37, 211, 102, 0.12); transform: translateY(-1px); }
.session-card:disabled { opacity: 0.75; cursor: wait; }
.session-card strong { display: block; color: inherit; }
.session-card small { display: block; margin-top: 2px; color: #7b8a84; }
.gateway-card-dark { background: #111b21; }
.gateway-card-dark .session-card { background: rgba(37, 211, 102, 0.08); }
.gateway-card-dark .session-card small { color: #a6b5af; }
@media(max-width: 720px) {
  .gateway-hero { align-items: flex-start; flex-wrap: wrap; }
  .gateway-hero .v-chip { margin-left: 70px; }
}
</style>
